import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Plane,
  Users,
  MapPin,
  Calendar,
  FileCheck,
  Building2,
  Info,
  CheckCircle2,
  Save,
  Send,
  Plus,
  Trash2,
  Clock,
  ArrowLeft,
  Loader2,
  AlertTriangle,
  Search,
  UserCheck,
  Wallet
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import SearchableProjectSelect from '../../components/SearchableProjectSelect';
import ManagerApprovalSelector from '../../components/travel/ManagerApprovalSelector';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const BOOKING_PREFERENCE_OPTIONS = {
  flight: [
    { value: 'economy', label: 'Economy Class' }
  ],
  train: [
    { value: '3ac', label: '3 AC' },
    { value: 'sleeper', label: 'Sleeper Class' }
  ],
  bus: [
    { value: 'ac_sleeper', label: 'Sleeper' },
    { value: 'ac_seater', label: 'Seater' }
  ],
  cab: [
    { value: 'sedan', label: 'Sedan (Dzire / Etios)' },
    { value: 'suv', label: 'SUV (Innova / Ertiga)' },
    { value: 'hatchback', label: 'Hatchback (Indica / WagonR)' }
  ]
};

const NewTravelRequest = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);

  // Active Entry Mode: 'single' | 'bulk'
  const [entryMode, setEntryMode] = useState('single');

  // Employee Directory for Passenger & Beneficiary Search
  const [employeesDirectory, setEmployeesDirectory] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  // Bulk Upload State
  const [bulkFile, setBulkFile] = useState(null);
  const [validatingBulk, setValidatingBulk] = useState(false);
  const [submittingBulk, setSubmittingBulk] = useState(false);
  const [bulkValidationResult, setBulkValidationResult] = useState(null);
  const [bulkBatchPurpose, setBulkBatchPurpose] = useState('Bulk Outstation Travel Request');

  // Outstanding Advance Pre-Check & Special Approval State
  const [advValidation, setAdvValidation] = useState(null);
  const [showSpecialApprovalModal, setShowSpecialApprovalModal] = useState(false);
  const [specialApprovalForm, setSpecialApprovalForm] = useState({
    urgency_reason: '',
    business_justification: '',
    expected_settlement_date: '',
  });

  // Unified Form Data State
  const [formData, setFormData] = useState({
    purpose: '',
    travel_type: 'flight',
    travel_category: 'Domestic',
    travel_facility_type: 'ticket_and_advance',
    booking_responsibility: 'company_booked',
    is_emergency: false,
    project: 'Internal',
    client: 'N/A',

    // Requester vs Beneficiary Employee alignment
    is_self_request: true,
    requested_on_behalf: false,
    beneficiary_employee_id: user?.employee_id || user?.id || user?._id || '',
    beneficiary_employee_code: user?.employee_code || user?.code || '',
    beneficiary_name: user?.name || user?.full_name || '',

    journey_details: [
      {
        segment_number: 1,
        from_city: '',
        to_city: '',
        departure_date: '',
        return_date: '',
        preferred_time: 'Morning (06:00 - 12:00)',
        travel_mode: 'flight',
        booking_preference: 'economy',
        class_entitlement: 'Economy Class'
      }
    ],

    passengers: [
      {
        employee_id: user?.employee_id || user?.id || user?._id || '',
        employee_code: user?.employee_code || user?.code || '',
        name: user?.name || user?.full_name || '',
        age: '',
        gender: 'Male',
        id_type: 'Aadhaar Card',
        id_number: '',
        is_guest: false,
      }
    ],

    department_id: '',
    department_name: '',
    department: '',
    project_id: '',
    project_code: '',
    project_name: '',
    project: '',
    remarks: '',

    advance_requested: false,
    advance_owner_employee_id: user?.employee_id || user?.id || user?._id || '',
    advance_owner_name: user?.name || '',
    advance_hotel: 0,
    advance_food: 0,
    advance_local_travel: 0,
    advance_misc: 0,
  });

  const [departments, setDepartments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loadingDepts, setLoadingDepts] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);

  // Approval Manager State
  const [eligibleManagers, setEligibleManagers] = useState([]);
  const [selectedApproverId, setSelectedApproverId] = useState('');
  const [loadingManagers, setLoadingManagers] = useState(false);

  useEffect(() => {
    const targetEmpId = formData.beneficiary_employee_id || user?.employee_id || user?.id || user?._id || '';
    if (targetEmpId) {
      setLoadingManagers(true);
      axios.get(`${API_URL}/approval-workflows/eligible-managers?employee_id=${targetEmpId}&doc_type=travel_request`, { withCredentials: true })
        .then(({ data }) => {
          const mgrList = data?.managers || [];
          setEligibleManagers(mgrList);
          if (mgrList.length > 0) {
            const defaultMgr = mgrList.find(m => m.hierarchy_level === 1 || m.relationship_to_employee === 'Reporting Manager') || mgrList[0];
            setSelectedApproverId(prev => (prev && mgrList.some(m => m.employee_id === prev) ? prev : defaultMgr.employee_id));
          }
        })
        .catch(() => setEligibleManagers([]))
        .finally(() => setLoadingManagers(false));
    }
  }, [formData.beneficiary_employee_id, user]);

  // Fetch company-scoped departments, projects, and employee directory
  useEffect(() => {
    const empCompanyId = user?.company_id || user?.companyId || '';
    setLoadingDepts(true);
    axios.get(`${API_URL}/masters/departments?company_id=${empCompanyId}&active_only=true`, { withCredentials: true })
      .then(({ data }) => setDepartments(Array.isArray(data) ? data : []))
      .catch(() => setDepartments([]))
      .finally(() => setLoadingDepts(false));

    setLoadingProjects(true);
    axios.get(`${API_URL}/masters/projects?company_id=${empCompanyId}&active_only=true`, { withCredentials: true })
      .then(({ data }) => setProjects(Array.isArray(data) ? data : []))
      .catch(() => setProjects([]))
      .finally(() => setLoadingProjects(false));

    setLoadingEmployees(true);
    axios.get(`${API_URL}/employees/minimal`, { withCredentials: true })
      .then(({ data }) => {
        const empList = Array.isArray(data) ? data : (data?.data || []);
        setEmployeesDirectory(empList);
      })
      .catch(() => setEmployeesDirectory([]))
      .finally(() => setLoadingEmployees(false));
  }, [user]);

  // Sync user info into first passenger & advance owner
  useEffect(() => {
    if (user?.name && (!formData.passengers[0]?.name || !formData.passengers[0]?.employee_id)) {
      const uEmpId = user?.employee_id || user?.id || user?._id || '';
      setFormData((prev) => ({
        ...prev,
        advance_owner_employee_id: prev.advance_owner_employee_id || uEmpId,
        advance_owner_name: prev.advance_owner_name || user.name,
        passengers: [
          {
            ...prev.passengers[0],
            employee_id: uEmpId,
            employee_code: user.employee_code || user.code || '',
            name: user.name,
          },
          ...prev.passengers.slice(1),
        ],
      }));
    }
  }, [user]);

  // Real-time Beneficiary Outstanding Advance Check Effect
  useEffect(() => {
    const benId = formData.advance_owner_employee_id || formData.beneficiary_employee_id || user?.employee_id || user?.id;
    if (formData.advance_requested && benId) {
      axios.get(`${API_URL}/advance-settlements/employee/${benId}/open-advances`, { withCredentials: true })
        .then(({ data }) => setAdvValidation(data))
        .catch((err) => console.error('Advance precheck error', err));
    } else {
      setAdvValidation(null);
    }
  }, [formData.advance_requested, formData.beneficiary_employee_id, formData.advance_owner_employee_id, user]);

  // Update specific journey leg segment
  const updateJourneyField = (idx, field, value) => {
    const nextLegs = [...formData.journey_details];
    nextLegs[idx][field] = value;

    // Auto-link next leg's from_city if to_city is updated
    if (field === 'to_city' && idx + 1 < nextLegs.length) {
      nextLegs[idx + 1].from_city = value;
    }

    // Auto-update booking preference when leg travel_mode changes
    if (field === 'travel_mode') {
      const opts = BOOKING_PREFERENCE_OPTIONS[value] || BOOKING_PREFERENCE_OPTIONS.flight;
      nextLegs[idx].booking_preference = opts[0]?.value || 'economy';
      nextLegs[idx].class_entitlement = opts[0]?.label || 'Standard Class';
    }

    setFormData({ ...formData, journey_details: nextLegs });
  };

  // Add Multi-City / Multi-Location Segment
  const addJourneyLeg = () => {
    const prevLegs = formData.journey_details;
    const lastLeg = prevLegs[prevLegs.length - 1];
    const autoFrom = lastLeg ? lastLeg.to_city : '';
    const autoDep = lastLeg ? lastLeg.departure_date : '';
    const autoMode = lastLeg ? lastLeg.travel_mode : (formData.travel_type !== 'multi_city' ? formData.travel_type : 'flight');

    const opts = BOOKING_PREFERENCE_OPTIONS[autoMode] || BOOKING_PREFERENCE_OPTIONS.flight;
    const newLeg = {
      segment_number: prevLegs.length + 1,
      from_city: autoFrom,
      to_city: '',
      departure_date: autoDep,
      return_date: '',
      preferred_time: 'Morning (06:00 - 12:00)',
      travel_mode: autoMode,
      booking_preference: opts[0]?.value || 'economy',
      class_entitlement: opts[0]?.label || 'Standard Class'
    };

    const nextLegs = [...prevLegs, newLeg];
    setFormData({
      ...formData,
      travel_type: nextLegs.length > 1 ? 'multi_city' : formData.travel_type,
      journey_details: nextLegs
    });
    toast.info(`Added Location #${nextLegs.length}`);
  };

  // Remove Segment Leg
  const removeJourneyLeg = (idx) => {
    if (formData.journey_details.length === 1) {
      toast.error('At least one travel segment is required.');
      return;
    }
    const filtered = formData.journey_details.filter((_, i) => i !== idx);
    // Re-link segment numbers
    const updated = filtered.map((leg, i) => ({ ...leg, segment_number: i + 1 }));
    setFormData({
      ...formData,
      travel_type: updated.length > 1 ? 'multi_city' : (formData.travel_type === 'multi_city' ? 'flight' : formData.travel_type),
      journey_details: updated
    });
  };

  // Update Passenger details
  const updatePassengerField = (idx, field, value) => {
    const nextPax = [...formData.passengers];
    nextPax[idx][field] = value;
    setFormData({ ...formData, passengers: nextPax });
  };

  // Validate & Search Passenger by Employee Code
  const handleSearchPassengerCode = async (idx, codeInput) => {
    const codeToSearch = (codeInput || formData.passengers[idx]?.search_code_input || formData.passengers[idx]?.employee_code || '').trim();
    if (!codeToSearch) {
      toast.error('Please enter an Employee Code.');
      return;
    }

    try {
      const { data } = await axios.get(
        `${API_URL}/travel-desk/validate-employee-code?code=${encodeURIComponent(codeToSearch)}`,
        { withCredentials: true }
      );

      const empIdStr = String(data.employee_id || data.id);
      const isDup = formData.passengers.some((p, i) => i !== idx && String(p.employee_id) === empIdStr);
      if (isDup) {
        toast.error('This employee has already been added as a passenger.');
        return;
      }

      const nextPax = [...formData.passengers];
      nextPax[idx] = {
        ...nextPax[idx],
        employee_id: empIdStr,
        employee_code: data.employee_code,
        name: data.employee_name,
        gender: data.gender || nextPax[idx].gender || 'Male',
        department: data.department || '',
        designation: data.designation || '',
        search_code_input: data.employee_code,
        is_validated: true,
        is_guest: false
      };

      setFormData({ ...formData, passengers: nextPax });
      toast.success(`Validated Employee: ${data.employee_name} (${data.employee_code})`);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Employee not found. Please enter a valid Employee Code.';
      toast.error(msg);
    }
  };

  // Validate & Search Advance Assigned Owner by Employee Code
  const handleSearchAdvanceOwnerCode = async (codeToSearch) => {
    const cleanCode = (codeToSearch || '').trim();
    if (!cleanCode) {
      toast.error('Please enter an Employee Code.');
      return;
    }

    try {
      const { data } = await axios.get(
        `${API_URL}/travel-desk/validate-employee-code?code=${encodeURIComponent(cleanCode)}`,
        { withCredentials: true }
      );

      setFormData({
        ...formData,
        advance_owner_employee_id: String(data.employee_id || data.id),
        advance_owner_name: data.employee_name,
        advance_owner_code: data.employee_code,
        advance_owner_input: data.employee_code
      });
      toast.success(`Advance Owner assigned: ${data.employee_name} (${data.employee_code})`);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Employee not found. Please enter a valid Employee Code.';
      toast.error(msg);
    }
  };

  const addPassenger = () => {
    setFormData({
      ...formData,
      passengers: [
        ...formData.passengers,
        {
          employee_id: '',
          employee_code: '',
          name: '',
          age: '',
          gender: 'Male',
          id_type: 'Aadhaar Card',
          id_number: '',
          is_guest: false
        },
      ],
    });
  };

  const removePassenger = (idx) => {
    if (formData.passengers.length === 1) return;
    setFormData({
      ...formData,
      passengers: formData.passengers.filter((_, i) => i !== idx),
    });
  };

  // Beneficiary Search Input State (Employee ID Search Only)
  const [beneficiaryCodeInput, setBeneficiaryCodeInput] = useState('');
  const [validatingBeneficiary, setValidatingBeneficiary] = useState(false);

  // Toggle Request Mode (Self vs On-Behalf)
  const handleToggleRequestMode = (isSelf) => {
    if (isSelf) {
      const selfEmpId = user?.employee_id || user?.id || user?._id || '';
      const selfCode = user?.employee_code || user?.code || '';
      const selfName = user?.name || user?.full_name || '';

      setBeneficiaryCodeInput('');
      setFormData((prev) => ({
        ...prev,
        is_self_request: true,
        requested_on_behalf: false,
        beneficiary_employee_id: selfEmpId,
        beneficiary_employee_code: selfCode,
        beneficiary_name: selfName,
        advance_owner_employee_id: selfEmpId,
        advance_owner_name: selfName,
        advance_owner_code: selfCode,
        passengers: [
          {
            ...prev.passengers[0],
            employee_id: selfEmpId,
            employee_code: selfCode,
            name: selfName,
            search_code_input: selfCode,
            is_validated: true,
            is_guest: false,
          },
          ...prev.passengers.slice(1)
        ]
      }));
      toast.info('Switched to Self Request mode.');
    } else {
      // Clear passenger #1 so logged-in user is not automatically set as passenger
      setFormData((prev) => ({
        ...prev,
        is_self_request: false,
        requested_on_behalf: true,
        beneficiary_employee_id: '',
        beneficiary_employee_code: '',
        beneficiary_name: '',
        advance_owner_employee_id: '',
        advance_owner_name: '',
        advance_owner_code: '',
        passengers: [
          {
            ...prev.passengers[0],
            employee_id: '',
            employee_code: '',
            name: '',
            search_code_input: '',
            is_validated: false,
            is_guest: false,
          },
          ...prev.passengers.slice(1)
        ]
      }));
      toast.info('Switched to On-Behalf Request mode. Please enter the Beneficiary Employee ID.');
    }
  };

  // Validate Beneficiary by exact Employee ID
  const handleValidateBeneficiaryCode = async (codeInput) => {
    const codeToSearch = (codeInput || beneficiaryCodeInput || '').trim();
    if (!codeToSearch) {
      toast.error('Please enter a complete Employee ID.');
      return;
    }

    try {
      setValidatingBeneficiary(true);
      const { data } = await axios.get(
        `${API_URL}/travel-desk/validate-employee-code?code=${encodeURIComponent(codeToSearch)}`,
        { withCredentials: true }
      );

      const bId = String(data.employee_id || data.id);
      const bCode = data.employee_code || codeToSearch;
      const bName = data.employee_name || data.name || '';

      setFormData((prev) => ({
        ...prev,
        is_self_request: false,
        requested_on_behalf: true,
        beneficiary_employee_id: bId,
        beneficiary_employee_code: bCode,
        beneficiary_name: bName,
        advance_owner_employee_id: bId,
        advance_owner_name: bName,
        advance_owner_code: bCode,
        passengers: [
          {
            ...prev.passengers[0],
            employee_id: bId,
            employee_code: bCode,
            name: bName,
            gender: data.gender || prev.passengers[0]?.gender || 'Male',
            department: data.department || prev.passengers[0]?.department || '',
            designation: data.designation || prev.passengers[0]?.designation || '',
            search_code_input: bCode,
            is_validated: true,
            is_guest: false,
          },
          ...prev.passengers.slice(1)
        ]
      }));

      setBeneficiaryCodeInput(bCode);
      toast.success(`Validated Beneficiary: ${bName} (${bCode})`);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Employee not found. Please enter a valid Employee Code.';
      toast.error(msg);
    } finally {
      setValidatingBeneficiary(false);
    }
  };

  // Download Bulk Upload CSV Template
  const handleDownloadBulkTemplate = async () => {
    try {
      const response = await axios.get(`${API_URL}/travel-desk/bulk-template`, {
        responseType: 'blob',
        withCredentials: true
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'bulk_travel_requests_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Downloaded Bulk Travel Requests CSV Template');
    } catch {
      toast.error('Failed to download bulk CSV template');
    }
  };

  // Validate Bulk CSV File
  const handleValidateBulkFile = async () => {
    if (!bulkFile) {
      toast.error('Please select a CSV file to validate.');
      return;
    }

    try {
      setValidatingBulk(true);
      const dataPayload = new FormData();
      dataPayload.append('file', bulkFile);

      const { data } = await axios.post(`${API_URL}/travel-desk/bulk-validate`, dataPayload, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });

      setBulkValidationResult(data);
      if (data.summary?.invalid_rows > 0) {
        toast.warning(`Validation completed with ${data.summary.invalid_rows} row error(s). Please inspect before submitting.`);
      } else {
        toast.success(`Validation successful! ${data.summary?.valid_rows || 0} valid record(s) ready for batch submission.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to validate bulk CSV file');
    } finally {
      setValidatingBulk(false);
    }
  };

  // Submit Bulk Batch
  const handleSubmitBulkBatch = async () => {
    if (!bulkValidationResult || !bulkValidationResult.items || bulkValidationResult.items.length === 0) {
      toast.error('No valid items ready for batch submission.');
      return;
    }

    try {
      setSubmittingBulk(true);
      const payload = {
        batch_purpose: bulkBatchPurpose || 'Bulk Corporate Travel Request',
        items: bulkValidationResult.items
      };

      const { data } = await axios.post(`${API_URL}/travel-desk/bulk-submit`, payload, {
        withCredentials: true
      });

      toast.success(data.message || `Bulk batch submitted successfully with ${data.created_count} travel request(s)!`);
      navigate('/travel/requests');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit bulk batch requests');
    } finally {
      setSubmittingBulk(false);
    }
  };

  // Primary Travel Mode Change handler
  const handlePrimaryTravelTypeChange = (newType) => {
    setFormData((prev) => {
      const updatedLegs = prev.journey_details.map((leg) => {
        const segMode = newType === 'multi_city' ? leg.travel_mode || 'flight' : newType;
        const opts = BOOKING_PREFERENCE_OPTIONS[segMode] || BOOKING_PREFERENCE_OPTIONS.flight;
        return {
          ...leg,
          travel_mode: segMode,
          booking_preference: opts[0]?.value || 'economy',
          class_entitlement: opts[0]?.label || 'Standard Class'
        };
      });

      return {
        ...prev,
        travel_type: newType,
        journey_details: updatedLegs
      };
    });
  };

  // Save Draft Handler
  const handleSaveDraft = async () => {
    if (submitting) return;
    try {
      setSubmitting(true);
      const payload = {
        ...formData,
        selected_approver_id: selectedApproverId,
        selected_manager_id: selectedApproverId,
        status: 'draft',
      };

      const { data } = await axios.post(`${API_URL}/travel-desk/requests`, payload, {
        withCredentials: true,
      });

      toast.success('Travel Request saved as draft successfully!');
      if (data.request?._id) {
        navigate(`/travel/request/${data.request._id}`);
      } else {
        navigate('/travel/requests');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save draft');
    } finally {
      setSubmitting(false);
    }
  };

  // Submit Handler
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (submitting) return;

    if (!formData.purpose.trim()) {
      toast.error('Please specify Travel Purpose.');
      return;
    }

    for (let i = 0; i < formData.journey_details.length; i++) {
      const leg = formData.journey_details[i];
      if (!leg.from_city.trim() || !leg.to_city.trim()) {
        toast.error(`Segment #${i + 1}: Please specify From and To cities.`);
        return;
      }
      if (!leg.departure_date) {
        toast.error(`Segment #${i + 1}: Please select Departure Date.`);
        return;
      }
    }

    if (!formData.passengers[0]?.name?.trim()) {
      toast.error('Please select details for Passenger 1.');
      return;
    }

    if (!formData.department_id && !formData.department_name) {
      toast.error('Please select a Department.');
      return;
    }
    if (!formData.project_id && !formData.project_name && !formData.project_code) {
      toast.error('Please select a Project.');
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        ...formData,
        selected_approver_id: selectedApproverId,
        selected_manager_id: selectedApproverId,
        status: 'submitted',
      };

      const { data } = await axios.post(`${API_URL}/travel-desk/requests`, payload, {
        withCredentials: true,
      });

      toast.success('Travel Request submitted successfully!');
      if (data.request?._id) {
        navigate(`/travel/request/${data.request._id}`);
      } else {
        navigate('/travel/requests');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit travel request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-[1200px] mx-auto font-sans pb-28" data-testid="new-travel-request-page">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-blue-950 text-white rounded-2xl p-6 shadow-xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-amber-500/20 text-amber-400 rounded-xl border border-amber-500/30 shrink-0">
            <Plane className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-xl font-black tracking-tight">New Corporate Travel Request</h1>
            <p className="text-xs text-blue-200 mt-0.5">
              Complete individual or bulk travel requisitions with dynamic approval matrix routing.
            </p>
          </div>
        </div>

        <button
          onClick={() => navigate('/travel/requests')}
          data-testid="btn-cancel-top"
          className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold text-xs rounded-xl border border-slate-700 transition flex items-center gap-1.5 shrink-0"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Cancel &amp; Back
        </button>
      </div>

      {/* Entry Mode Switcher Tabs */}
      <div className="bg-slate-100 p-1 rounded-xl flex items-center gap-1 border border-slate-200 w-fit">
        <button
          type="button"
          onClick={() => setEntryMode('single')}
          className={`px-4 py-2 rounded-lg text-xs font-bold transition flex items-center gap-2 ${
            entryMode === 'single'
              ? 'bg-white text-slate-900 shadow-sm border border-slate-200'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <Plane className="w-4 h-4 text-blue-600" /> Single Request Form
        </button>
        <button
          type="button"
          onClick={() => setEntryMode('bulk')}
          className={`px-4 py-2 rounded-lg text-xs font-bold transition flex items-center gap-2 ${
            entryMode === 'bulk'
              ? 'bg-white text-slate-900 shadow-sm border border-slate-200'
              : 'text-slate-600 hover:text-slate-900'
          }`}
        >
          <Users className="w-4 h-4 text-amber-600" /> Bulk Request Upload (Batch)
        </button>
      </div>

      {entryMode === 'bulk' ? (
        /* BULK CSV UPLOAD INTERFACE */
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 space-y-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-slate-100 pb-4">
            <div>
              <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                <Users className="w-5 h-5 text-amber-600" /> Bulk Corporate Travel Submission
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Upload a CSV spreadsheet with multiple employee travel requests. Each row is validated independently and assigned to its own dynamic approval matrix.
              </p>
            </div>
            <button
              type="button"
              onClick={handleDownloadBulkTemplate}
              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl border border-slate-300 transition flex items-center gap-2 shrink-0"
            >
              Download CSV Template
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Batch Purpose / Project Name</label>
              <input
                type="text"
                value={bulkBatchPurpose}
                onChange={(e) => setBulkBatchPurpose(e.target.value)}
                placeholder="e.g. Q4 Site Inspection Tour Batch"
                className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs font-semibold outline-none focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Upload CSV File</label>
              <input
                type="file"
                accept=".csv"
                onChange={(e) => setBulkFile(e.target.files?.[0] || null)}
                className="w-full px-3 py-1.5 bg-slate-50 border border-slate-300 rounded-xl text-xs font-medium text-slate-700 file:mr-3 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-bold file:bg-blue-600 file:text-white hover:file:bg-blue-700 cursor-pointer"
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleValidateBulkFile}
              disabled={validatingBulk || !bulkFile}
              className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-extrabold text-xs rounded-xl shadow transition flex items-center gap-2"
            >
              {validatingBulk ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Validate CSV Data
            </button>
            {bulkFile && (
              <span className="text-xs text-slate-500 font-medium">Selected file: {bulkFile.name}</span>
            )}
          </div>

          {/* Validation Summary Cards */}
          {bulkValidationResult && (
            <div className="space-y-4 pt-2">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <div className="text-[11px] font-bold uppercase text-slate-500">Total Rows Evaluated</div>
                  <div className="text-2xl font-black text-slate-900 mt-1">{bulkValidationResult.summary?.total_rows || 0}</div>
                </div>
                <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                  <div className="text-[11px] font-bold uppercase text-emerald-700">Valid Transactions</div>
                  <div className="text-2xl font-black text-emerald-800 mt-1">{bulkValidationResult.summary?.valid_rows || 0}</div>
                </div>
                <div className="p-4 bg-rose-50 rounded-xl border border-rose-200">
                  <div className="text-[11px] font-bold uppercase text-rose-700">Row Errors</div>
                  <div className="text-2xl font-black text-rose-800 mt-1">{bulkValidationResult.summary?.invalid_rows || 0}</div>
                </div>
              </div>

              {/* Parsed / Validated Table Preview */}
              <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                <div className="p-3 bg-slate-100 font-bold text-xs text-slate-700 border-b border-slate-200 flex justify-between items-center">
                  <span>Validation Preview Table</span>
                  <span className="text-[11px] text-slate-500 font-normal">Only valid rows will be created on submission</span>
                </div>
                <div className="overflow-x-auto max-h-[350px]">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200 text-slate-600 font-bold">
                        <th className="p-2.5">Row #</th>
                        <th className="p-2.5">Beneficiary Employee</th>
                        <th className="p-2.5">Purpose</th>
                        <th className="p-2.5">Mode</th>
                        <th className="p-2.5">Route</th>
                        <th className="p-2.5">Departure</th>
                        <th className="p-2.5">Advance</th>
                        <th className="p-2.5">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 font-medium">
                      {(bulkValidationResult.items || []).map((row, idx) => (
                        <tr key={idx} className={row.valid ? 'hover:bg-slate-50' : 'bg-rose-50/50 hover:bg-rose-50'}>
                          <td className="p-2.5 font-bold text-slate-700">{row.row_number}</td>
                          <td className="p-2.5 font-bold text-slate-900">
                            {row.beneficiary_employee_name || '—'}
                            <span className="block text-[10px] text-slate-400 font-mono">{row.beneficiary_employee_code || row.employee_code}</span>
                          </td>
                          <td className="p-2.5">{row.purpose || '—'}</td>
                          <td className="p-2.5 capitalize">{row.travel_type || 'flight'}</td>
                          <td className="p-2.5">{row.from_city} &rarr; {row.to_city}</td>
                          <td className="p-2.5">{row.departure_date}</td>
                          <td className="p-2.5 font-mono">₹ {(Number(row.advance_hotel) || 0) + (Number(row.advance_food) || 0) + (Number(row.advance_local_travel) || 0) + (Number(row.advance_misc) || 0)}</td>
                          <td className="p-2.5">
                            {row.valid ? (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-emerald-100 text-emerald-800 border border-emerald-300">
                                Valid
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-rose-100 text-rose-800 border border-rose-300" title={row.errors?.join('; ')}>
                                {row.errors?.join(', ') || 'Invalid'}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex justify-end pt-2">
                <button
                  type="button"
                  onClick={handleSubmitBulkBatch}
                  disabled={submittingBulk || !bulkValidationResult.summary?.valid_rows}
                  className="px-7 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-black text-xs rounded-xl shadow-md transition flex items-center gap-2"
                >
                  {submittingBulk ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Submit Batch ({bulkValidationResult.summary?.valid_rows || 0} Valid Requests)
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* SINGLE REQUEST FORM */
        <>
          {/* REQUEST OWNERSHIP & BENEFICIARY SELECTION */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 space-y-4">
            <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
              <div className="p-1.5 bg-indigo-50 text-indigo-600 rounded-lg">
                <UserCheck className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900 text-base">Request Ownership &amp; Beneficiary Employee</h3>
                <p className="text-[11px] text-slate-500">Configure whether you are raising this travel request for yourself or on-behalf of another employee.</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-2">Request Mode *</label>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-xs font-bold text-slate-800 cursor-pointer">
                    <input
                      type="radio"
                      name="request_mode"
                      checked={formData.is_self_request}
                      onChange={() => handleToggleRequestMode(true)}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-500 cursor-pointer"
                    />
                    Self Request (I am traveling)
                  </label>
                  <label className="flex items-center gap-2 text-xs font-bold text-slate-800 cursor-pointer">
                    <input
                      type="radio"
                      name="request_mode"
                      checked={!formData.is_self_request}
                      onChange={() => handleToggleRequestMode(false)}
                      className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                    />
                    On-Behalf Request (For Another Employee)
                  </label>
                </div>
              </div>

              {!formData.is_self_request && (
                <div>
                  <label className="block text-xs font-bold text-indigo-900 mb-1">Enter Beneficiary Employee ID *</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="e.g. S2248"
                      value={beneficiaryCodeInput}
                      onChange={(e) => setBeneficiaryCodeInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleValidateBeneficiaryCode();
                        }
                      }}
                      className="flex-1 px-3 py-2 bg-indigo-50/50 border border-indigo-300 rounded-xl text-xs font-mono font-bold text-indigo-950 outline-none focus:border-indigo-600 uppercase"
                      data-testid="input-beneficiary-emp-id"
                    />
                    <button
                      type="button"
                      onClick={() => handleValidateBeneficiaryCode()}
                      disabled={validatingBeneficiary}
                      className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-bold text-xs rounded-xl transition flex items-center gap-1.5 shrink-0 shadow-sm"
                      data-testid="btn-validate-beneficiary"
                    >
                      {validatingBeneficiary ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />} Validate ID
                    </button>
                  </div>
                </div>
              )}
            </div>

            {!formData.is_self_request && formData.beneficiary_name && (
              <div className="p-4 bg-indigo-50/80 rounded-xl border border-indigo-200 text-indigo-950 text-xs font-bold space-y-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <span className="flex items-center gap-2 text-indigo-900 text-sm font-extrabold">
                    <UserCheck className="w-4 h-4 text-indigo-600" />
                    Beneficiary / Primary Traveller: <span className="underline">{formData.beneficiary_name}</span> ({formData.beneficiary_employee_code})
                  </span>
                  <span className="text-[10px] bg-emerald-600 text-white font-extrabold px-2.5 py-0.5 rounded-full uppercase tracking-wider flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Validated
                  </span>
                </div>
                <div className="text-[11px] text-indigo-700 font-normal border-t border-indigo-200/60 pt-1.5 flex justify-between flex-wrap gap-2">
                  <span>Passenger #1, Advance Owner &amp; Ticket Booking are linked to this beneficiary.</span>
                  <span className="font-semibold text-indigo-900">Requester: {user?.name || 'Self'} ({user?.employee_code || user?.code || 'Self'})</span>
                </div>
              </div>
            )}
          </div>

          {/* SECTION 1: TRAVEL & JOURNEY DETAILS */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 space-y-5" data-testid="section-travel-details">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-blue-50 text-blue-600 rounded-lg">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 text-base">Section 1 — Travel &amp; Journey Details</h3>
                  <p className="text-[11px] text-slate-500">Define your travel purpose, primary mode, origin, destination, and route dates.</p>
                </div>
              </div>
            </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">Travel Purpose *</label>
            <input
              type="text"
              placeholder="e.g. Client Onboarding Meeting"
              value={formData.purpose}
              onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
              className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs font-medium outline-none focus:border-blue-500 focus:bg-white transition"
              data-testid="input-travel-purpose"
              required
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">Primary Travel Mode *</label>
            <select
              value={formData.travel_type}
              onChange={(e) => handlePrimaryTravelTypeChange(e.target.value)}
              className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs font-semibold outline-none focus:border-blue-500 transition"
              data-testid="select-travel-type"
            >
              <option value="flight">Flight</option>
              <option value="train">Train</option>
              <option value="bus">Bus</option>
              <option value="cab">Cab / Taxi</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">Travel Category</label>
            <select
              value={formData.travel_category || 'Domestic'}
              onChange={(e) => {
                const cat = e.target.value;
                setFormData({
                  ...formData,
                  travel_category: cat,
                  is_international: cat === 'International'
                });
              }}
              className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs font-semibold outline-none focus:border-blue-500 transition"
              data-testid="select-travel-category"
            >
              <option value="Domestic">Domestic</option>
              <option value="International">International</option>
            </select>
          </div>
        </div>

        {/* UNIFIED TRAVEL FACILITY OPTIONS */}
        <div className="space-y-2 pt-2 border-t border-slate-100" data-testid="field-travel-facility-type">
          <div className="flex items-center justify-between">
            <label className="block text-xs font-bold text-slate-800 flex items-center gap-1.5">
              <span>Travel Facility Request Type *</span>
              <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-bold">Unified Lifecycle</span>
            </label>
            <span className="text-[11px] text-slate-500 font-medium">
              Select desired facilities under a single Travel ID
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { id: 'ticket_only', label: '1. Ticket Booking Only', desc: 'Company books travel tickets only. No advance requested.', adv: false, resp: 'company_booked' },
              { id: 'advance_only', label: '2. Travel Advance Only', desc: 'Request travel cash advance. Employee books own tickets.', adv: true, resp: 'self_booked' },
              { id: 'ticket_and_advance', label: '3. Ticket + Advance', desc: 'Company books tickets AND disburses travel cash advance.', adv: true, resp: 'company_booked' },
              { id: 'approval_only', label: '4. Travel Approval Only', desc: 'Employee spends own money & claims later under same Travel ID.', adv: false, resp: 'self_booked' },
            ].map((fac) => (
              <button
                key={fac.id}
                type="button"
                onClick={() => setFormData(prev => ({
                  ...prev,
                  travel_facility_type: fac.id,
                  advance_requested: fac.adv,
                  booking_responsibility: fac.resp,
                }))}
                className={`p-3 rounded-xl border text-left transition-all cursor-pointer ${
                  formData.travel_facility_type === fac.id
                    ? 'bg-blue-50 border-blue-600 ring-2 ring-blue-500/20 shadow-sm'
                    : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="font-bold text-xs text-slate-900 mb-1">{fac.label}</div>
                <div className="text-[11px] text-slate-500 leading-tight">{fac.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {/* TICKET BOOKING RESPONSIBILITY */}
        <div className="space-y-2 pt-2 border-t border-slate-100" data-testid="field-booking-responsibility">
          <div className="flex items-center justify-between">
            <label className="block text-xs font-bold text-slate-700">
              Ticket Booking Responsibility *
            </label>
            <span className="text-[11px] text-slate-400 font-medium">
              Choose who arranges and books journey tickets
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, booking_responsibility: 'self_booked' }))}
              className={`p-3.5 rounded-xl border text-left transition-all flex items-start gap-3 cursor-pointer ${
                formData.booking_responsibility === 'self_booked'
                  ? 'bg-indigo-50/80 border-indigo-500 ring-2 ring-indigo-500/20 shadow-sm'
                  : 'bg-slate-50 border-slate-200 hover:border-slate-300'
              }`}
              data-testid="option-self-booked"
            >
              <input
                type="radio"
                name="booking_responsibility"
                value="self_booked"
                checked={formData.booking_responsibility === 'self_booked'}
                onChange={() => setFormData(prev => ({ ...prev, booking_responsibility: 'self_booked' }))}
                className="mt-0.5 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
              />
              <div>
                <div className="font-bold text-xs text-slate-900 flex items-center gap-1.5">
                  <span>Self-Booked</span>
                  <span className="text-[10px] font-bold text-indigo-700 bg-indigo-100/80 px-2 py-0.5 rounded-full">
                    Employee Arranges Ticket
                  </span>
                </div>
                <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">
                  Employee arranges own journey tickets directly. Request travel advances freely without mandatory company ticket preferences.
                </p>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, booking_responsibility: 'company_booked' }))}
              className={`p-3.5 rounded-xl border text-left transition-all flex items-start gap-3 cursor-pointer ${
                formData.booking_responsibility === 'company_booked'
                  ? 'bg-blue-50/80 border-blue-500 ring-2 ring-blue-500/20 shadow-sm'
                  : 'bg-slate-50 border-slate-200 hover:border-slate-300'
              }`}
              data-testid="option-company-booked"
            >
              <input
                type="radio"
                name="booking_responsibility"
                value="company_booked"
                checked={formData.booking_responsibility === 'company_booked'}
                onChange={() => setFormData(prev => ({ ...prev, booking_responsibility: 'company_booked' }))}
                className="mt-0.5 text-blue-600 focus:ring-blue-500 cursor-pointer"
              />
              <div>
                <div className="font-bold text-xs text-slate-900 flex items-center gap-1.5">
                  <span>Company-Booked</span>
                  <span className="text-[10px] font-bold text-blue-700 bg-blue-100/80 px-2 py-0.5 rounded-full">
                    Company Arranges Ticket
                  </span>
                </div>
                <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">
                  Travel Desk or designated corporate booking controller books tickets and itineraries in line with entitlement policy.
                </p>
              </div>
            </button>
          </div>
        </div>

        {/* APPROVAL MANAGER SELECTION WITH EMPLOYEE ID LOOKUP */}
        <ManagerApprovalSelector
          selectedApproverId={selectedApproverId}
          onSelectApprover={(empId, mgrObj) => setSelectedApproverId(empId)}
          eligibleManagers={eligibleManagers}
          targetEmployeeId={formData.beneficiary_employee_id || user?.employee_id || user?.id}
          loadingManagers={loadingManagers}
          docType="travel_request"
          label="Manager Approval Selection (Outstation Conveyance) *"
        />

        <div className="flex flex-wrap gap-6 pt-1 border-t border-slate-100/80">
          <label className="flex items-center gap-2 text-xs font-bold text-slate-700 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={formData.is_emergency}
              onChange={(e) => setFormData({ ...formData, is_emergency: e.target.checked })}
              className="rounded text-amber-600 focus:ring-amber-500 w-4 h-4 cursor-pointer"
              data-testid="chk-emergency"
            />
            Emergency / Urgent Travel
          </label>
        </div>

        {/* Dynamic Route & Dates Subsection */}
        <div className="space-y-3 pt-2">
          <div className="flex items-center justify-between">
            <h4 className="font-bold text-xs uppercase text-slate-500 tracking-wider flex items-center gap-1.5">
              <Calendar className="w-4 h-4 text-blue-500" /> Route &amp; Dates ({formData.journey_details.length} Segment{formData.journey_details.length > 1 ? 's' : ''})
            </h4>
            <button
              type="button"
              onClick={addJourneyLeg}
              className="px-3.5 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-extrabold text-xs rounded-xl transition flex items-center gap-1 border border-indigo-200 shadow-sm"
              data-testid="btn-add-segment"
            >
              <Plus className="w-3.5 h-3.5" /> Add Location
            </button>
          </div>

          {formData.journey_details.map((leg, idx) => {
            const currentMode = leg.travel_mode || formData.travel_type || 'flight';
            const prefOptions = BOOKING_PREFERENCE_OPTIONS[currentMode] || BOOKING_PREFERENCE_OPTIONS.flight;

            return (
              <div key={idx} className="bg-slate-50/80 p-4 rounded-xl border border-slate-200 space-y-3" data-testid={`route-segment-${idx}`}>
                <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                  <div className="flex items-center gap-2 font-bold text-xs text-slate-900">
                    <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-[10px] flex items-center justify-center">
                      {idx + 1}
                    </span>
                    <span>Segment #{idx + 1}: {leg.from_city || 'Origin'} &rarr; {leg.to_city || 'Destination'}</span>
                  </div>
                  {formData.journey_details.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeJourneyLeg(idx)}
                      className="text-rose-600 hover:text-rose-800 text-xs font-bold flex items-center gap-1"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Remove Segment
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-6 gap-3">
                  <div>
                    <label className="block text-[11px] font-bold text-slate-600 mb-1">From City *</label>
                    <input
                      type="text"
                      placeholder="Origin (e.g. Chennai)"
                      value={leg.from_city}
                      onChange={(e) => updateJourneyField(idx, 'from_city', e.target.value)}
                      className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-semibold outline-none focus:border-blue-500"
                      data-testid={`input-from-city-${idx}`}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-600 mb-1">To City *</label>
                    <input
                      type="text"
                      placeholder="Destination (e.g. Bengaluru)"
                      value={leg.to_city}
                      onChange={(e) => updateJourneyField(idx, 'to_city', e.target.value)}
                      className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-semibold outline-none focus:border-blue-500"
                      data-testid={`input-to-city-${idx}`}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-600 mb-1">Departure Date *</label>
                    <input
                      type="date"
                      value={leg.departure_date}
                      onChange={(e) => updateJourneyField(idx, 'departure_date', e.target.value)}
                      className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-semibold outline-none focus:border-blue-500"
                      data-testid={`input-departure-date-${idx}`}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-600 mb-1">Return Date</label>
                    <input
                      type="date"
                      value={leg.return_date || ''}
                      onChange={(e) => updateJourneyField(idx, 'return_date', e.target.value)}
                      className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-semibold outline-none focus:border-blue-500"
                      data-testid={`input-return-date-${idx}`}
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-600 mb-1">Segment Mode</label>
                    <select
                      value={leg.travel_mode || 'flight'}
                      onChange={(e) => updateJourneyField(idx, 'travel_mode', e.target.value)}
                      className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-semibold outline-none focus:border-blue-500"
                    >
                      <option value="flight">Flight</option>
                      <option value="train">Train</option>
                      <option value="bus">Bus</option>
                      <option value="cab">Cab / Taxi</option>
                    </select>
                  </div>

                  {formData.booking_responsibility === 'company_booked' ? (
                    <div>
                      <label className="block text-[11px] font-bold text-slate-600 mb-1">Booking Preference *</label>
                      <select
                        value={leg.booking_preference || prefOptions[0]?.value}
                        onChange={(e) => {
                          const opt = prefOptions.find(o => o.value === e.target.value);
                          updateJourneyField(idx, 'booking_preference', e.target.value);
                          if (opt) updateJourneyField(idx, 'class_entitlement', opt.label);
                        }}
                        className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-semibold outline-none focus:border-blue-500"
                        data-testid={`select-booking-pref-${idx}`}
                      >
                        {prefOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div>
                      <label className="block text-[11px] font-bold text-slate-500 mb-1">Ticket Arrangement</label>
                      <div className="w-full px-3 py-1.5 bg-indigo-50 border border-indigo-200 text-indigo-700 rounded-lg text-xs font-semibold flex items-center gap-1.5 truncate" data-testid={`self-booked-indicator-${idx}`}>
                        <span className="w-2 h-2 rounded-full bg-indigo-600 shrink-0"></span>
                        Self-Booked (Employee)
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* SECTION 2: PASSENGERS */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 space-y-5" data-testid="section-passengers">
        <div className="flex justify-between items-center border-b border-slate-100 pb-3">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-blue-50 text-blue-600 rounded-lg">
              <Users className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold text-slate-900 text-base">Section 2 — Passenger Manifest</h3>
              <p className="text-[11px] text-slate-500">Search and select authorized company employees or guests traveling in this request.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={addPassenger}
            className="px-3.5 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-600 font-bold text-xs rounded-xl transition flex items-center gap-1"
            data-testid="btn-add-passenger"
          >
            <Plus className="w-3.5 h-3.5" /> Add Passenger
          </button>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2.5">
          <Info className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
          <div className="text-xs text-amber-800 leading-relaxed">
            <span className="font-bold">Role Distinction Notice:</span> Passenger 1 is the <strong>Main Traveller / Beneficiary</strong> who owns all advances, expenses, and claim reimbursements. Any Additional Passengers are included for <strong>ticket and journey coordination only</strong> and cannot claim expenses or advances under this Travel Request ID.
          </div>
        </div>

        <div className="space-y-4">
          {formData.passengers.map((pax, idx) => (
            <div key={idx} className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-3" data-testid={`passenger-card-${idx}`}>
              <div className="flex justify-between items-center">
                <span className="font-bold text-xs text-slate-800 flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-[10px] flex items-center justify-center font-bold">
                    {idx + 1}
                  </span>
                  Passenger #{idx + 1} {pax.employee_code && <span className="font-mono text-blue-600">({pax.employee_code})</span>}
                </span>
                {formData.passengers.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removePassenger(idx)}
                    className="text-xs text-rose-600 font-bold hover:underline flex items-center gap-1"
                    data-testid={`btn-remove-pax-${idx}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Remove
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                {/* Employee Code Search Input */}
                <div className="md:col-span-2 space-y-1">
                  <label className="block text-[11px] font-bold text-slate-600">Enter Employee Code *</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="e.g. S2248"
                      value={pax.search_code_input !== undefined ? pax.search_code_input : (pax.employee_code || '')}
                      onChange={(e) => updatePassengerField(idx, 'search_code_input', e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleSearchPassengerCode(idx);
                        }
                      }}
                      className="flex-1 px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-mono font-bold outline-none focus:border-blue-500 uppercase"
                      data-testid={`input-passenger-code-${idx}`}
                    />
                    <button
                      type="button"
                      onClick={() => handleSearchPassengerCode(idx)}
                      className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs rounded-lg flex items-center gap-1 shadow-sm shrink-0"
                      data-testid={`btn-search-pax-${idx}`}
                    >
                      <Search className="w-3.5 h-3.5" /> Validate Code
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-slate-600 mb-1">Employee / Passenger Name</label>
                  <input
                    type="text"
                    placeholder="Validated Name"
                    value={pax.name || ''}
                    onChange={(e) => updatePassengerField(idx, 'name', e.target.value)}
                    className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-semibold outline-none focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-slate-600 mb-1">Gender</label>
                  <select
                    value={pax.gender || 'Male'}
                    onChange={(e) => updatePassengerField(idx, 'gender', e.target.value)}
                    className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-semibold outline-none focus:border-blue-500"
                  >
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                {/* Validated Details Banner */}
                {pax.employee_id && (
                  <div className="md:col-span-4 p-2.5 bg-emerald-50 rounded-lg border border-emerald-200 text-[11px] text-emerald-900 flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                      <span>
                        <b>Validated Passenger:</b> {pax.name} <span className="font-mono text-emerald-700 font-bold">({pax.employee_code})</span> {pax.department ? `• ${pax.department}` : ''}
                      </span>
                    </div>
                    <span className="font-mono text-[10px] text-slate-500">ID: {pax.employee_id}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* SECTION 3: DEPARTMENT & PROJECT SCOPE */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 space-y-4">
        <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
          <div className="p-1.5 bg-blue-50 text-blue-600 rounded-lg">
            <Building2 className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-bold text-slate-900 text-base">Section 3 — Cost Scope &amp; Organization Assignment</h3>
            <p className="text-[11px] text-slate-500">Assign corporate department and project cost center for budget accounting.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">Department *</label>
            {loadingDepts ? (
              <div className="text-xs text-slate-400 py-2">Loading departments...</div>
            ) : (
              <select
                value={formData.department_id || ''}
                onChange={(e) => {
                  const selId = e.target.value;
                  const found = departments.find(d => String(d.id || d._id) === String(selId));
                  setFormData({
                    ...formData,
                    department_id: selId,
                    department_name: found ? found.name : formData.department_name,
                    department: found ? found.name : formData.department,
                  });
                }}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs font-semibold outline-none focus:border-blue-500"
                required
              >
                <option value="">-- Select Department --</option>
                {departments.map((d) => (
                  <option key={d.id || d._id} value={d.id || d._id}>
                    {d.name} {d.code ? `(${d.code})` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">Project *</label>
            {loadingProjects ? (
              <div className="text-xs text-slate-400 py-2">Loading projects...</div>
            ) : (
              <SearchableProjectSelect
                projects={projects}
                value={formData.project_id || ''}
                onChange={(found) => {
                  setFormData({
                    ...formData,
                    project_id: found ? (found.id || found._id) : '',
                    project_name: found ? (found.name || found.project_name) : '',
                    project_code: found ? (found.code || found.project_code) : '',
                    project: found ? (found.name || found.project_name) : '',
                  });
                }}
                required
              />
            )}
          </div>
        </div>
      </div>

      {/* SECTION 4: TRAVEL ADVANCE REQUISITION */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 space-y-4">
        <div className="flex items-center justify-between border-b border-slate-100 pb-3">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-amber-50 text-amber-600 rounded-lg">
              <Wallet className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold text-slate-900 text-base">Section 4 — Travel Advance Requisition</h3>
              <p className="text-[11px] text-slate-500">Request pre-travel funds for hotel, food, conveyance, or misc expenses.</p>
            </div>
          </div>

          <label className="flex items-center gap-2 font-bold text-xs text-slate-800 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.advance_requested}
              onChange={(e) => setFormData({ ...formData, advance_requested: e.target.checked })}
              className="w-4 h-4 rounded text-blue-600 cursor-pointer"
            />
            Request Travel Advance
          </label>
        </div>

        {formData.advance_requested && (
          <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-4">
            {advValidation && advValidation.is_blocked && (
              <div className="p-4 bg-amber-50 border border-amber-300 rounded-xl space-y-2 animate-in fade-in">
                <div className="flex items-center gap-2 font-bold text-amber-900 text-xs">
                  <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                  <span>Outstanding Travel Advance Restriction</span>
                </div>
                <p className="text-xs text-amber-800">
                  {advValidation.blocking_reason}
                </p>
                {advValidation.open_advances && advValidation.open_advances.length > 0 && (
                  <div className="text-[11px] bg-white p-2.5 rounded-lg border border-amber-200 font-mono text-slate-700">
                    <div><b>Ref #:</b> {advValidation.open_advances[0].finance_ref}</div>
                    <div><b>Date:</b> {advValidation.open_advances[0].disbursement_date} | <b>Purpose:</b> {advValidation.open_advances[0].purpose}</div>
                    <div><b>Advance Amount:</b> ₹{advValidation.open_advances[0].advance_amount?.toLocaleString()} | <b>Outstanding Balance:</b> ₹{advValidation.open_advances[0].pending_balance?.toLocaleString()}</div>
                  </div>
                )}
                {advValidation.special_approval_allowed && (
                  <div className="pt-1 flex items-center justify-between">
                    <span className="text-[11px] text-amber-900 font-medium">Urgent travel? Include Special Advance Approval request.</span>
                    <button
                      type="button"
                      onClick={() => {
                        const nextState = !formData.request_special_approval;
                        setFormData(prev => ({ ...prev, request_special_approval: nextState }));
                        if (nextState) {
                          toast.info("Special Advance Approval will be requested upon submission.");
                        }
                      }}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                        formData.request_special_approval
                          ? 'bg-emerald-600 text-white shadow-sm'
                          : 'bg-amber-700 hover:bg-amber-800 text-white'
                      }`}
                    >
                      {formData.request_special_approval ? '✓ Special Approval Requested' : 'Request Special Approval'}
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-2">
              <label className="block text-[11px] font-bold text-slate-700">Advance Assigned Owner *</label>
              <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                {/* Select from Passengers */}
                <div className="flex-1 w-full">
                  <span className="text-[10px] text-slate-500 font-semibold block mb-0.5">Select from Passengers:</span>
                  <select
                    value={formData.advance_owner_employee_id || ''}
                    onChange={(e) => {
                      const selEmpId = e.target.value;
                      const foundPax = formData.passengers.find(p => String(p.employee_id) === String(selEmpId));
                      if (foundPax && foundPax.employee_id) {
                        setFormData({
                          ...formData,
                          advance_owner_employee_id: foundPax.employee_id,
                          advance_owner_name: foundPax.name,
                          advance_owner_code: foundPax.employee_code,
                          advance_owner_input: foundPax.employee_code
                        });
                      } else {
                        setFormData({
                          ...formData,
                          advance_owner_employee_id: user?.employee_id || user?.id || '',
                          advance_owner_name: user?.name || 'Employee',
                          advance_owner_code: user?.employee_code || 'EMP',
                          advance_owner_input: user?.employee_code || ''
                        });
                      }
                    }}
                    className="w-full px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-semibold outline-none focus:border-blue-500"
                    data-testid="select-advance-owner-passenger"
                  >
                    <option value={user?.employee_id || user?.id}>Self: {user?.name || 'Requester'} ({user?.employee_code || 'EMP'})</option>
                    {formData.passengers.filter(p => p.employee_id).map((pax, i) => (
                      <option key={i} value={pax.employee_id}>
                        {pax.name} ({pax.employee_code || 'EMP'})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="text-xs font-bold text-slate-400 self-center uppercase">OR</div>

                {/* Search Code for Advance Owner */}
                <div className="flex-1 w-full">
                  <span className="text-[10px] text-slate-500 font-semibold block mb-0.5">Search Employee Code:</span>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="e.g. S2248"
                      value={formData.advance_owner_input !== undefined ? formData.advance_owner_input : (formData.advance_owner_code || '')}
                      onChange={(e) => setFormData({ ...formData, advance_owner_input: e.target.value })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleSearchAdvanceOwnerCode(formData.advance_owner_input);
                        }
                      }}
                      className="flex-1 px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-mono font-bold uppercase outline-none focus:border-blue-500"
                      data-testid="input-advance-owner-code"
                    />
                    <button
                      type="button"
                      onClick={() => handleSearchAdvanceOwnerCode(formData.advance_owner_input)}
                      className="px-3 py-1.5 bg-amber-600 hover:bg-amber-500 text-white font-bold text-xs rounded-lg flex items-center gap-1 shadow-sm shrink-0"
                      data-testid="btn-search-advance-owner"
                    >
                      <Search className="w-3.5 h-3.5" /> Validate
                    </button>
                  </div>
                </div>
              </div>

              {formData.advance_owner_name && (
                <div className="p-2.5 bg-amber-50 rounded-lg border border-amber-200 text-[11px] text-amber-950 flex items-center justify-between flex-wrap gap-2 font-medium mt-1">
                  <div className="flex items-center gap-2">
                    <Wallet className="w-4 h-4 text-amber-600 shrink-0" />
                    <span>
                      <b>Assigned Advance Owner:</b> {formData.advance_owner_name} <span className="font-mono text-amber-900 font-bold">({formData.advance_owner_code || 'EMP'})</span>
                    </span>
                  </div>
                  <span className="font-mono text-[10px] text-slate-500">Owner ID: {formData.advance_owner_employee_id}</span>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="block text-[11px] font-bold text-slate-600 mb-1">Hotel (₹)</label>
                <input
                  type="number"
                  placeholder="0.00"
                  value={formData.advance_hotel}
                  onChange={(e) => setFormData({ ...formData, advance_hotel: Number(e.target.value) })}
                  className="w-full px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-mono font-bold"
                />
              </div>
              <div>
                <label className="block text-[11px] font-bold text-slate-600 mb-1">Food (₹)</label>
                <input
                  type="number"
                  placeholder="0.00"
                  value={formData.advance_food}
                  onChange={(e) => setFormData({ ...formData, advance_food: Number(e.target.value) })}
                  className="w-full px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-mono font-bold"
                />
              </div>
              <div>
                <label className="block text-[11px] font-bold text-slate-600 mb-1">Local Travel (₹)</label>
                <input
                  type="number"
                  placeholder="0.00"
                  value={formData.advance_local_travel}
                  onChange={(e) => setFormData({ ...formData, advance_local_travel: Number(e.target.value) })}
                  className="w-full px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-mono font-bold"
                />
              </div>
              <div>
                <label className="block text-[11px] font-bold text-slate-600 mb-1">Misc (₹)</label>
                <input
                  type="number"
                  placeholder="0.00"
                  value={formData.advance_misc}
                  onChange={(e) => setFormData({ ...formData, advance_misc: Number(e.target.value) })}
                  className="w-full px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-mono font-bold"
                />
              </div>
            </div>

            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 text-amber-950 font-bold text-xs flex justify-between items-center">
              <span>Total Advance Requested:</span>
              <span className="font-mono text-sm text-amber-700">
                ₹ {((Number(formData.advance_hotel) || 0) + (Number(formData.advance_food) || 0) + (Number(formData.advance_local_travel) || 0) + (Number(formData.advance_misc) || 0)).toLocaleString('en-IN')}
              </span>
            </div>
            </div>
        )}
      </div>

      {/* FORM ACTION FOOTER */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-md border-t border-slate-200 p-4 z-40 flex justify-end gap-3 shadow-lg">
        <button
          type="button"
          onClick={handleSaveDraft}
          disabled={submitting}
          className="px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-extrabold text-xs rounded-xl transition flex items-center gap-1.5"
          data-testid="btn-save-draft"
        >
          <Save className="w-4 h-4" /> Save Draft
        </button>

        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting}
          className="px-7 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-black text-xs rounded-xl shadow-md transition flex items-center gap-1.5"
          data-testid="btn-submit-request"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Submit Travel Request
        </button>
      </div>
        </>
      )}
    </div>
  );
};

export default NewTravelRequest;
